import { useRef, useEffect, useState } from "react";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "300px",
};

interface MapViewProps {
  defaultLat: number;
  defaultLng: number;
  onSelectLocation: (lat: number, lng: number, address: string) => void;
  showSearchBar?: boolean;
}

export default function MapView({
  defaultLat,
  defaultLng,
  onSelectLocation,
  showSearchBar = false,
}: MapViewProps) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_KEY,
    libraries: ["places"],
  });

  const [position, setPosition] = useState<{ lat: number; lng: number }>({
    lat: defaultLat,
    lng: defaultLng,
  });
  const [searchValue, setSearchValue] = useState("");
  const autocompleteRef = useRef<any>(null);

  useEffect(() => {
    if (!isLoaded || !navigator.geolocation || !window.google) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPosition({ lat, lng });

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results?.[0]) {
            const address = results[0].formatted_address;
            setSearchValue(address);
            onSelectLocation(lat, lng, address);
          }
        });
      },
      () => setPosition({ lat: defaultLat, lng: defaultLng })
    );
  }, [isLoaded]);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const address = place.formatted_address || place.name || "";
    setPosition({ lat, lng });
    setSearchValue(address);
    onSelectLocation(lat, lng, address);
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    if (!lat || !lng) return;

    setPosition({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const address = results[0].formatted_address;
        setSearchValue(address);
        onSelectLocation(lat, lng, address);
      }
    });
  };

  if (!isLoaded) return <p>Loading map...</p>;

  return (
    <div>
      {showSearchBar && (
        <Autocomplete
          onLoad={(ref) => (autocompleteRef.current = ref)}
          onPlaceChanged={onPlaceChanged}
          options={{ componentRestrictions: { country: "in" } }}
          
        >
          <input
            type="text"
            placeholder="Search a location"
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </Autocomplete>
      )}


      <GoogleMap
        mapContainerStyle={containerStyle}
        center={position}
        zoom={15}
        onClick={handleMapClick}
      >
        <Marker position={position} />
      </GoogleMap>
    </div>
  );
}
